import { useState, useEffect } from "react";
import { C, S, font, fontCond, CURRENT_SEASON } from "../constants";
import { Avatar, DeadlineChip, PlayerPopover } from "../components/ui";
import { RoundTimePicker } from "../lib/courtAvailability";
import { getBlockStatus, getChallengeStatus, getPlayerStats, isExpired, isChallengerBlocked, fmtDate, shortName, getInactivityPenalty, getNextInactivityThreshold } from "../lib/helpers";

// ── Pagination helper ─────────────────────────────────────────────────────────
function Pagination({ page, total, perPage, onChange }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  const btnStyle = active => ({
    minWidth: 32, height: 32, borderRadius: 6,
    border: "1px solid " + (active ? C.primary : C.border),
    background: active ? C.primary : "transparent",
    color: active ? "#fff" : C.text,
    fontFamily: fontCond, fontWeight: 700, fontSize: 13,
    cursor: active ? "default" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px",
  });
  const navStyle = disabled => ({
    ...btnStyle(false),
    color: disabled ? C.border : C.muted,
    cursor: disabled ? "default" : "pointer",
  });
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
      <button style={navStyle(page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
        <button key={p} style={btnStyle(p === page)} onClick={() => p !== page && onChange(p)}>{p}</button>
      ))}
      <button style={navStyle(page === pages)} disabled={page === pages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

// ── Position Evolution Chart ───────────────────────────────────────────────────
function PositionChart({ me, myResults, isMobile, gender }) {
  const [posHistory, setPosHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    import("../lib/supabase").then(({ dbLoadPositionHistory }) => {
      dbLoadPositionHistory(me.id, gender).then(h => { setPosHistory(h); setLoading(false); });
    }).catch(() => setLoading(false));
  }, [me?.id]);

  const cardStyle = isMobile ? S.cardMobile : { ...S.card, width: "calc(50% - 8px)", maxWidth: "calc(50% - 8px)", boxSizing: "border-box", flexShrink: 0 };

  if (loading) return (
    <div style={cardStyle}>
      <div style={S.title}>📈 Evolução de Posição</div>
      <div style={{ color: C.muted, fontSize: 13 }}>Carregando…</div>
    </div>
  );

  let positions, labels;

  if (posHistory && posHistory.length > 0) {
    const chronological = [...posHistory];
    positions = chronological.map(h => h.position);
    labels = chronological.map(h => { const d = new Date(h.date); return d.getDate() + "/" + (d.getMonth() + 1); });
    positions.push(me.position);
    labels.push("Atual");
  } else {
    const myRsorted = [...myResults].sort((a, b) => new Date(a.date) - new Date(b.date));
    if (myRsorted.length === 0) return (
      <div style={cardStyle}>
        <div style={S.title}>📈 Evolução de Posição</div>
        <div style={{ color: C.muted, fontSize: 13 }}>Nenhuma partida registrada ainda.</div>
      </div>
    );
    let pos = me.position;
    const resultsCopy = [...myRsorted].reverse();
    positions = [pos];
    resultsCopy.forEach(r => {
      const isChallenger = r.challengerName === me.name;
      if (r.type === "wo-challenged")      { if (isChallenger) pos += 1; else pos = Math.max(1, pos - 5); }
      else if (r.type === "wo-challenger") { if (!isChallenger) pos += 1; else pos = Math.max(1, pos - 5); }
      else if (r.winner === "challenger")  { if (isChallenger) pos++; else pos = Math.max(1, pos - 1); }
      else                                  { if (!isChallenger) pos++; else pos = Math.max(1, pos - 1); }
      positions.unshift(pos);
    });
    labels = myRsorted.map(r => { const d = new Date(r.date); return d.getDate() + "/" + (d.getMonth() + 1); });
    labels.unshift("Início");
  }

  if (positions.length < 2) return (
    <div style={cardStyle}>
      <div style={S.title}>📈 Evolução de Posição</div>
      <div style={{ color: C.muted, fontSize: 13 }}>Histórico insuficiente.</div>
    </div>
  );

  const maxP = Math.max(...positions), minP = Math.min(...positions);
  const range = maxP - minP || 1;
  const W = 280, H = 80, pad = 12;
  const pts = positions.map((p, i) => {
    const x = pad + (i / (positions.length - 1 || 1)) * (W - pad * 2);
    const y = pad + ((p - minP) / range) * (H - pad * 2);
    return [x, y];
  });

  return (
    <div style={cardStyle}>
      <div style={S.title}>📈 Evolução de Posição</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H+20}`} style={{ overflow: "visible" }}>
        {pts.slice(1).map(([x2,y2], i) => {
          const [x1,y1] = pts[i];
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.primary} strokeWidth="2.5" strokeLinecap="round"/>;
        })}
        {pts.map(([x,y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="4" fill={C.primary}/>
            <text x={x} y={y-8} textAnchor="middle" fontSize="9" fill={C.muted}>#{positions[i]}</text>
            <text x={x} y={H+16} textAnchor="middle" fontSize="8" fill={C.muted}>{labels[i]}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── Inactivity Card ───────────────────────────────────────────────────────────
function InactivityCard({ me, isMobile, inactivityHistory }) {
  const isCurrentlyInactive = me.inactiveUntil && new Date(me.inactiveUntil) > new Date();

  const history = (inactivityHistory || []).filter(h => h.playerId === me.id);
  const histDays = history.reduce((acc, h) => acc + h.days, 0);
  const nowTs = new Date();
  const currentRun = isCurrentlyInactive && me.inactiveSince
    ? Math.max(0, Math.floor((Math.min(nowTs, new Date(me.inactiveUntil)) - new Date(me.inactiveSince + "T12:00:00")) / 86400000))
    : 0;
  const ytd = histDays + currentRun;

  const { penalty, faixa, label } = getInactivityPenalty(ytd);
  const next = getNextInactivityThreshold(ytd);
  const daysToNext = next ? next.threshold - ytd : null;
  const faixaColor = penalty === 0 ? C.success : penalty <= 10 ? C.warning : C.primary;

  return (
    <div style={isMobile ? S.cardMobile : S.card}>
      <div style={S.title}>😴 Inatividade</div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: faixaColor + "18",
          border: "2px solid " + faixaColor,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <span style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 20, color: faixaColor, lineHeight: 1 }}>{ytd}</span>
          <span style={{ fontSize: 9, color: faixaColor, fontWeight: 700, letterSpacing: 0.5 }}>dias</span>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
            Faixa {faixa} — {penalty === 0 ? "Sem penalidade" : `−${penalty} posições`}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{label}</div>
          {isCurrentlyInactive && (
            <div style={{ ...S.badge(C.warning), marginTop: 6, display: "inline-block" }}>
              Inativo até {new Date(me.inactiveUntil).toLocaleDateString("pt-BR")}
              {me.inactiveSince && ` · desde ${new Date(me.inactiveSince + "T12:00:00").toLocaleDateString("pt-BR")}`}
            </div>
          )}
        </div>
      </div>

      {next && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, marginBottom: 4 }}>
            <span>{ytd} dias acumulados</span>
            <span>próxima penalidade em {daysToNext} dia{daysToNext !== 1 ? "s" : ""} (−{next.penalty} pos.)</span>
          </div>
          <div style={{ height: 6, borderRadius: 4, background: C.border, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 4, background: faixaColor,
              width: `${Math.min(100, (ytd / next.threshold) * 100)}%`,
              transition: "width .4s",
            }}/>
          </div>
        </div>
      )}

      {!next && ytd > 0 && (
        <div style={{ ...S.danger, fontSize: 12 }}>
          ⚠️ Você atingiu a faixa máxima de penalidade ({penalty} posições). Fique atento ao limite de 180 dias.
        </div>
      )}

      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
        Contagem cumulativa da temporada {CURRENT_SEASON}.
      </div>

      {/* Histórico de períodos */}
      <div style={{ marginTop: 16, borderTop: "1px solid " + C.border, paddingTop: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 10 }}>Histórico de períodos</div>
        {history.length === 0 && (
          <div style={{ fontSize: 12, color: C.muted }}>Nenhum período registrado.</div>
        )}
        {history.length > 0 && (
          isMobile ? (
            <div>
              {history.map(h => (
                <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderTop: "1px solid " + C.border, gap: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {new Date(h.since + "T12:00:00").toLocaleDateString("pt-BR")} → {new Date(h.until + "T12:00:00").toLocaleDateString("pt-BR")}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{h.days} dia{h.days !== 1 ? "s" : ""}</div>
                  </div>
                  <span style={{ fontSize: 11, color: h.penalty > 0 ? C.primary : C.success, fontWeight: 700 }}>
                    {h.penalty > 0 ? `−${h.penalty} pos.` : "Sem penalidade"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Início</th>
                  <th style={S.th}>Fim</th>
                  <th style={S.th}>Dias</th>
                  <th style={S.th}>Faixa</th>
                  <th style={S.th}>Penalidade</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td style={S.td}>{new Date(h.since + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                    <td style={S.td}>{new Date(h.until + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{h.days}</td>
                    <td style={S.td}>Faixa {h.faixa}</td>
                    <td style={{ ...S.td, color: h.penalty > 0 ? C.primary : C.success, fontWeight: 600 }}>
                      {h.penalty > 0 ? `−${h.penalty} posições` : "Sem penalidade"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  );
}

// ── MinhaScreen ───────────────────────────────────────────────────────────────
const NOTIFS_PER_PAGE = 10;

export default function MinhaScreen({
  me, state, isMobile, allChallenges,
  currentUser,
  pendingConfirm, pendingChallengerConfirm,
  myChallenges, myResults, myNotifs, unreadCount,
  eligibleTargets, activeAsChallenger,
  challengeTarget, setChallengeTarget,
  challengeDates, setChallengeDates,
  setProfileEmail, setProfilePhone, setProfileModal,
  setOldPass, setNewPass1, setNewPass2,
  setConfirmModal, setPickedDate,
  setCounterModal, setCounterDates,
  setRefuseModal, setMediationModal,
  proposeChallenge,
  cancelChallengeByChallenger,
  inactivityHistory,
  gender,
}) {
  const [notifsPage, setNotifsPage] = useState(1);

  const st = getPlayerStats(me.name, state.results, me.id);
  const isLoggedIn = !!currentUser;

  const notifsSlice = myNotifs.slice((notifsPage - 1) * NOTIFS_PER_PAGE, notifsPage * NOTIFS_PER_PAGE);

  const bl = getBlockStatus(me), cs = getChallengeStatus(me, state.challenges);
  const statusLabel = bl === "inativo" ? "Inativo" : (cs === "desafiado" || cs === "ambos" || bl === "bloqueado") ? "Indisponível" : "Disponível";
  const statusColor = bl === "inativo" ? "#666666" : (cs === "desafiado" || cs === "ambos" || bl === "bloqueado") ? "#e53e3e" : C.accent;

  // ── email obrigatório
  const missingEmail = !me.email || me.email.trim() === "";
  const openProfile = () => { setProfileEmail(me.email || ""); setProfilePhone(me.phone || ""); setProfileModal(true); };

  return (
    <div>

      {/* ── Banner: email obrigatório ─────────────────────────────────────── */}
      {missingEmail && (
        <div style={{
          background: "#fffbe6", border: "1.5px solid #f0c000", borderRadius: 10,
          padding: "12px 16px", marginBottom: 12,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#7a5c00" }}>Email não cadastrado</div>
            <div style={{ fontSize: 12, color: "#a07800", marginTop: 2 }}>
              Cadastre seu email para poder propor desafios.
            </div>
          </div>
          <button
            onClick={openProfile}
            style={{ fontSize: 12, padding: "7px 16px", borderRadius: 8, fontWeight: 700,
              color: "#5a4000", background: "#f0c000", border: "none", cursor: "pointer", flexShrink: 0 }}
          >
            Cadastrar email
          </button>
        </div>
      )}

      {/* ── Card de perfil ───────────────────────────────────────────────── */}
      <div style={{ ...isMobile ? S.cardMobile : S.card, display: "flex", alignItems: "stretch", gap: isMobile ? 16 : 24, flexWrap: isMobile ? "wrap" : "nowrap" }}>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <div style={{ width: isMobile ? 72 : 88, height: isMobile ? 72 : 88, borderRadius: "50%", overflow: "hidden", border: "3px solid " + C.primary, flexShrink: 0 }}>
            {me.photo
              ? <img src={me.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              : <Avatar player={me} size={isMobile ? 72 : 88}/>
            }
          </div>
          <button onClick={openProfile} style={{ ...S.btn(C.muted, true), fontSize: 11, padding: "5px 12px", minHeight: 28 }}>
            ✏️ Editar perfil
          </button>
        </div>

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontFamily: fontCond, fontWeight: 900, fontSize: isMobile ? 28 : 36, color: C.primary, lineHeight: 1, marginBottom: 2 }}>
            #{me.position}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <span style={{ fontFamily: fontCond, fontWeight: 800, fontSize: isMobile ? 16 : 20, color: C.text }}>{me.name}</span>
            <span style={S.badge(statusColor)}>{statusLabel}</span>
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {me.email && (
              <a href={`mailto:${me.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>{me.email}</span>
              </a>
            )}
            {me.phone && (
              <a href={`https://wa.me/55${me.phone.replace(/\D/g,"")}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#1a7a3c" }}>{me.phone}</span>
              </a>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: isMobile ? 8 : 10, alignItems: "stretch", flexShrink: 0, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          {[
            { label: "Vitórias", value: st.wins, color: C.success },
            { label: "Derrotas", value: st.losses, color: "#e53e3e" },
            { label: "Taxa", value: st.winRate + "%", color: C.accent, sub: st.total + " partidas" },
          ].map(({ label, value, color, sub }) => (
            <div key={label} style={{
              background: C.cardAlt, border: "1px solid " + C.border,
              borderRadius: 10, padding: isMobile ? "10px 14px" : "12px 18px",
              textAlign: "center", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", minWidth: isMobile ? 72 : 80,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
              <div style={{ fontFamily: fontCond, fontWeight: 900, fontSize: isMobile ? 24 : 28, color, lineHeight: 1 }}>{value}</div>
              {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{sub}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Confirmações pendentes (desafiado) */}
      {pendingConfirm.length > 0 && (
        <div style={isMobile ? S.cardMobile : S.card}>
          <div style={S.title}>📬 Confirmar Data</div>
          {pendingConfirm.map(c => {
            const chr = state.players.find(p => p.id === c.challengerId);
            return (
              <div key={c.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid " + C.border }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                  Desafio de {chr ? chr.name : c.challengerName}
                  <DeadlineChip proposedAt={c.proposedAt} style={{ marginLeft: 8 }}/>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {c.dates.filter(d => d).map((d, i) => (
                    <button key={i} onClick={() => { setConfirmModal(c); setPickedDate(d); }}
                      style={{ background: C.accent + "11", border: "1px solid " + C.accent + "44", borderRadius: 8, padding: "10px 14px", cursor: "pointer", textAlign: "left", fontSize: 13, color: C.accent, fontWeight: 600, fontFamily: font, minHeight: 44, WebkitTapHighlightColor: "transparent" }}>
                      ✅ {fmtDate(d)}
                    </button>
                  ))}
                  <button onClick={() => { setCounterModal(c); setCounterDates(["", "", ""]); }}
                    style={{ background: "#fff8f0", border: "1px solid #e67e2244", borderRadius: 8, padding: "10px 14px", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#e67e22", fontWeight: 600, fontFamily: font, minHeight: 44, WebkitTapHighlightColor: "transparent" }}>
                    🔄 Propor novas datas
                  </button>
                  <button onClick={() => setRefuseModal(c)}
                    style={{ background: "#fff0f0", border: "1px solid " + C.primary + "44", borderRadius: 8, padding: "10px 14px", cursor: "pointer", textAlign: "left", fontSize: 13, color: C.primary, fontWeight: 600, fontFamily: font, minHeight: 44, WebkitTapHighlightColor: "transparent" }}>
                    ❌ Recusar desafio
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmações pendentes (desafiante — contra-proposta) */}
      {pendingChallengerConfirm.length > 0 && (
        <div style={isMobile ? S.cardMobile : S.card}>
          <div style={S.title}>🔄 Novas Datas Propostas</div>
          {pendingChallengerConfirm.map(c => {
            const chd = state.players.find(p => p.id === c.challengedId);
            return (
              <div key={c.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid " + C.border }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                  {chd ? chd.name : c.challengedName} propôs novas datas
                  <DeadlineChip proposedAt={c.proposedAt} style={{ marginLeft: 8 }}/>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {c.dates.filter(d => d).map((d, i) => (
                    <button key={i} onClick={() => { setConfirmModal(c); setPickedDate(d); }}
                      style={{ background: C.accent + "11", border: "1px solid " + C.accent + "44", borderRadius: 8, padding: "10px 14px", cursor: "pointer", textAlign: "left", fontSize: 13, color: C.accent, fontWeight: 600, fontFamily: font, minHeight: 44, WebkitTapHighlightColor: "transparent" }}>
                      ✅ {fmtDate(d)}
                    </button>
                  ))}
                  <button onClick={() => setMediationModal(c)}
                    style={{ background: "#fff8f0", border: "1px solid #e67e2244", borderRadius: 8, padding: "10px 14px", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#e67e22", fontWeight: 600, fontFamily: font, minHeight: 44, WebkitTapHighlightColor: "transparent" }}>
                    ⚠️ Datas não me atendem — acionar suporte
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Chart + desafios ativos */}
      <div style={isMobile ? {} : { display: "flex", gap: 16, alignItems: "flex-start" }}>
        <PositionChart me={me} myResults={myResults} isMobile={isMobile} gender={gender}/>

        <div style={isMobile ? S.cardMobile : { ...S.card, width: "calc(50% - 8px)", maxWidth: "calc(50% - 8px)", boxSizing: "border-box", flexShrink: 0, alignSelf: "flex-start", overflow: "hidden" }}>
          <div style={S.title}>⚔️ Meus Desafios</div>
          {myChallenges.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>Nenhum desafio ativo.</div>}
          {myChallenges.map(c => {
            const chr = state.players.find(p => p.id === c.challengerId);
            const chd = state.players.find(p => p.id === c.challengedId);
            const role = c.challengerId === me.id ? "Desafiante" : "Desafiado";
            const opp = c.challengerId === me.id ? chd : chr;
            const expired = isExpired(c);
            return (
              <div key={c.id} style={{ padding: "12px 0", borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <PlayerPopover player={opp || {id:0,name:"?",position:"-"}} isLoggedIn={isLoggedIn}>
                  <Avatar player={opp || {id:0,name:"?"}} size={36}/>
                </PlayerPopover>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{role} vs {opp ? shortName(opp.name) : "-"} <span style={{ color: C.muted }}>(#{opp ? opp.position : "-"})</span></div>
                  <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {c.status === "agendado"
                      ? <span style={S.badge(C.success)}>📅 {fmtDate(c.confirmedDate)}</span>
                      : expired
                      ? <span style={S.badge(C.primary)}>Expirado</span>
                      : c.status === "aguardando_confirmacao" && role === "Desafiante" && c.lastProposedBy === "challenged"
                      ? <><span style={S.badge("#e67e22")}>Novas datas propostas</span><DeadlineChip proposedAt={c.proposedAt}/></>
                      : c.status === "em_mediacao"
                      ? <span style={S.badge("#8e44ad")}>Em mediação</span>
                      : <><span style={S.badge(C.warning)}>⏳ Aguardando</span><DeadlineChip proposedAt={c.proposedAt}/></>
                    }
                  </div>
                </div>
                {/* Botão cancelar: só para o desafiante, antes do desafiado responder */}
                {role === "Desafiante" && c.status === "aguardando_confirmacao" && c.lastProposedBy === "challenger" && (
                  <button
                    onClick={() => cancelChallengeByChallenger(c.id)}
                    style={{ fontSize: 11, padding: "5px 10px", borderRadius: 7, fontWeight: 700,
                      color: C.primary, background: "#fff0f0", border: "1px solid " + C.primary + "44",
                      cursor: "pointer", flexShrink: 0, WebkitTapHighlightColor: "transparent" }}>
                    ✕ Cancelar
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Propor desafio */}
      <div style={isMobile ? S.cardMobile : S.card}>
        <div style={S.title}>⚔️ Propor Desafio</div>
        {missingEmail ? (
          <div style={{
            background: "#fffbe6", border: "1.5px solid #f0c000", borderRadius: 8,
            padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#7a5c00" }}>Email necessário para desafiar</div>
              <div style={{ fontSize: 12, color: "#a07800", marginTop: 2 }}>Cadastre seu email no perfil para liberar esta função.</div>
            </div>
            <button onClick={openProfile}
              style={{ fontSize: 11, padding: "6px 12px", borderRadius: 8, fontWeight: 700,
                color: "#5a4000", background: "#f0c000", border: "none", cursor: "pointer", flexShrink: 0 }}>
              Cadastrar
            </button>
          </div>
        ) : (isChallengerBlocked(me) || activeAsChallenger >= 1) ? (
          <div style={S.danger}>Você está indisponível para propor desafios{(me.blockedAsChallenger && new Date(me.blockedAsChallenger) > new Date()) ? " até " + new Date(me.blockedAsChallenger).toLocaleDateString("pt-BR") : ""}.</div>
        ) : eligibleTargets.length === 0 ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Nenhum adversário elegível no momento.</div>
        ) : (
          <div>
            <label style={S.label}>Adversário</label>
            <select style={S.select} value={challengeTarget} onChange={e => setChallengeTarget(e.target.value)}>
              <option value="">Selecione o adversário</option>
              {eligibleTargets.map(p => <option key={p.id} value={p.id}>{p.position}. {p.name}</option>)}
            </select>
            <label style={S.label}>3 opções de data</label>
            {challengeDates.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: C.muted, minWidth: 52, flexShrink: 0 }}>Opção {i+1}</span>
                <RoundTimePicker
                  value={d}
                  onChange={v => { const nd = [...challengeDates]; nd[i] = v; setChallengeDates(nd); }}
                  style={{ ...S.input, marginBottom: 0 }}
                  allChallenges={allChallenges}
                  otherDates={challengeDates.filter((_, j) => j !== i)}
                  blockedDates={state.blockedDates}
                  holidays={state.holidays || []}
                  courtSlots={state.courtSlots || []}
                  blockedPeriods={state.blockedPeriods || []}
                />
              </div>
            ))}
            <button style={{ ...S.btn(), width: "100%", marginTop: 8 }} onClick={proposeChallenge}>Enviar Desafio</button>
          </div>
        )}
      </div>

      {/* Card de Inatividade */}
      <InactivityCard me={me} isMobile={isMobile} inactivityHistory={inactivityHistory}/>

      {/* Histórico */}
      <div style={isMobile ? S.cardMobile : S.card}>
        <div style={S.title}>📋 Meu Histórico</div>
        {myResults.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>Nenhum resultado ainda.</div>}
        {isMobile ? (
          <div>
            {[...myResults].reverse().map((r, i) => {
              const role = r.challengerName === me.name ? "Desafiante" : "Desafiado";
              const opp  = r.challengerName === me.name ? r.challengedName : r.challengerName;
              const won  = (role==="Desafiante"&&r.winner==="challenger")||(role==="Desafiado"&&r.winner==="challenged")||(role==="Desafiado"&&r.type==="wo-challenger")||(role==="Desafiante"&&r.type==="wo-challenged");
              const isWO = r.type === "wo-challenged" || r.type === "wo-challenger";
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                  <span style={{ ...S.badge(won ? C.success : "#e53e3e"), flexShrink: 0 }}>{isWO ? "WO" : (won ? "V" : "D")}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>vs {opp}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{role} · {new Date(r.date).toLocaleDateString("pt-BR")}</div>
                  </div>
                  {r.score && <span style={{ fontFamily: fontCond, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{r.score}</span>}
                </div>
              );
            })}
          </div>
        ) : (
          <table style={S.table}>
            <thead><tr><th style={S.th}>Data</th><th style={S.th}>Adversário</th><th style={S.th}>Papel</th><th style={S.th}>Placar</th><th style={S.th}>Resultado</th></tr></thead>
            <tbody>
              {[...myResults].reverse().map((r, i) => {
                const role = r.challengerName === me.name ? "Desafiante" : "Desafiado";
                const opp  = r.challengerName === me.name ? r.challengedName : r.challengerName;
                const won  = (role==="Desafiante"&&r.winner==="challenger")||(role==="Desafiado"&&r.winner==="challenged")||(role==="Desafiado"&&r.type==="wo-challenger")||(role==="Desafiante"&&r.type==="wo-challenged");
                const isWO = r.type === "wo-challenged" || r.type === "wo-challenger";
                return (
                  <tr key={i}>
                    <td style={S.td}>{new Date(r.date).toLocaleDateString("pt-BR")}</td>
                    <td style={S.td}>{opp}</td>
                    <td style={{ ...S.td, color: C.muted }}>{role}</td>
                    <td style={{ ...S.td, fontFamily: fontCond, fontWeight: 700 }}>{r.score || "—"}</td>
                    <td style={S.td}><span style={S.badge(won ? C.success : "#e53e3e")}>{isWO ? "WO" : (won ? "Vitória" : "Derrota")}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Notificações — paginado */}
      <div style={isMobile ? S.cardMobile : S.card}>
        <div style={{ ...S.title, display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span>
            🔔 Notificações
            {unreadCount > 0 && <span style={{ ...S.badge(C.primary), marginLeft: 8 }}>{unreadCount} nova{unreadCount > 1 ? "s" : ""}</span>}
          </span>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{myNotifs.length} no total</span>
        </div>
        {myNotifs.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>Nenhuma notificação.</div>}
        {notifsSlice.map(n => (
          <div key={n.id} style={{ padding: "12px 0", borderBottom: "1px solid " + C.border, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: n.read ? C.border : C.primary, marginTop: 5, flexShrink: 0 }}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: n.read ? C.muted : C.text, fontWeight: n.read ? 400 : 600, lineHeight: 1.5 }}>{n.text}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{fmtDate(n.date)}</div>
            </div>
          </div>
        ))}
        <Pagination page={notifsPage} total={myNotifs.length} perPage={NOTIFS_PER_PAGE} onChange={setNotifsPage} />
      </div>
    </div>
  );
}
